import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import HeaderTwo from '../../components/HeaderTwo';
import { BreadcrumbBox } from '../../components/common/Breadcrumb';
import FooterTwo from '../../components/FooterTwo';
import { Styles } from './styles/account.js';
import { AUTH_REGISTER_ENDPOINT } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: ''
};

const isValidEmail = (value) => /.+@.+\..+/.test(value);

function Register() {
    const history = useHistory();
    const [formValues, setFormValues] = useState(initialForm);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const { refreshUser } = useAuth();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setFormError('');
    setFormSuccess('');
  };

  const validate = () => {
    const errors = {};
    if (!formValues.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formValues.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formValues.email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formValues.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formValues.username.trim()) {
      errors.username = 'Username is required';
    }
    if (!formValues.password) {
      errors.password = 'Password is required';
    } else if (formValues.password.length < 6) {
      errors.password = 'Password should be at least 6 characters';
    }
    if (!formValues.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formValues.password !== formValues.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const persistAuth = ({ token, user }) => {
    if (token) {
      localStorage.setItem('authToken', token);
    }
    if (user) {
      localStorage.setItem('authUser', JSON.stringify(user));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const response = await fetch(AUTH_REGISTER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formValues.firstName.trim(),
          lastName: formValues.lastName.trim(),
          email: formValues.email.trim(),
          username: formValues.username.trim(),
          password: formValues.password
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Unable to register. Please try again.');
      }

      const payload = await response.json();
      persistAuth(payload);
      if (typeof refreshUser === 'function') {
        await refreshUser();
      }
      setFormSuccess('Registration successful! Redirecting to courses…');
      setTimeout(() => {
        history.push(`${process.env.PUBLIC_URL || ''}/courses`);
      }, 1000);
    } catch (error) {
      console.error(error);
      setFormError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Styles>
      <div className="main-wrapper registration-page">
        <HeaderTwo />
        <BreadcrumbBox title="Registration" />

        <section className="registration-area">
          <Container>
            <Row>
              <Col md="12">
                <div className="registration-box">
                  <div className="registration-title text-center">
                    <h3>Registration</h3>
                  </div>
                  {formError && <Alert variant="danger">{formError}</Alert>}
                  {formSuccess && <Alert variant="success">{formSuccess}</Alert>}
                  <form className="form" onSubmit={handleSubmit} noValidate>
                    <p className={`form-control ${fieldErrors.firstName ? 'error' : ''}`}>
                      <label htmlFor="registration_fname">First Name</label>
                      <input
                        type="text"
                        placeholder="First name"
                        id="registration_fname"
                        name="firstName"
                        value={formValues.firstName}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.firstName && <span className="registration_input-msg">{fieldErrors.firstName}</span>}
                    </p>
                    <p className={`form-control ${fieldErrors.lastName ? 'error' : ''}`}>
                      <label htmlFor="registration_lname">Last Name</label>
                      <input
                        type="text"
                        placeholder="Last name"
                        id="registration_lname"
                        name="lastName"
                        value={formValues.lastName}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.lastName && <span className="registration_input-msg">{fieldErrors.lastName}</span>}
                    </p>
                    <p className={`form-control ${fieldErrors.email ? 'error' : ''}`}>
                      <label htmlFor="registration_email">Email Address</label>
                      <input
                        type="email"
                        placeholder="Email address"
                        id="registration_email"
                        name="email"
                        value={formValues.email}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.email && <span className="registration_input-msg">{fieldErrors.email}</span>}
                    </p>
                    <p className={`form-control ${fieldErrors.username ? 'error' : ''}`}>
                      <label htmlFor="registration_user">User Name</label>
                      <input
                        type="text"
                        placeholder="Username"
                        id="registration_user"
                        name="username"
                        value={formValues.username}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.username && <span className="registration_input-msg">{fieldErrors.username}</span>}
                    </p>
                    <p className={`form-control ${fieldErrors.password ? 'error' : ''}`}>
                      <label htmlFor="registration_password">Password</label>
                      <input
                        type="password"
                        placeholder="*******"
                        id="registration_password"
                        name="password"
                        value={formValues.password}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.password && <span className="registration_input-msg">{fieldErrors.password}</span>}
                    </p>
                    <p className={`form-control ${fieldErrors.confirmPassword ? 'error' : ''}`}>
                      <label htmlFor="registration_cpassword">Confirm Password</label>
                      <input
                        type="password"
                        placeholder="Confirm password"
                        id="registration_cpassword"
                        name="confirmPassword"
                        value={formValues.confirmPassword}
                        onChange={handleChange}
                        disabled={submitting}
                      />
                      {fieldErrors.confirmPassword && (
                        <span className="registration_input-msg">{fieldErrors.confirmPassword}</span>
                      )}
                    </p>
                    <button type="submit" disabled={submitting}>
                      {submitting ? <Spinner animation="border" size="sm" /> : 'Register Now'}
                    </button>
                  </form>
                  <div className="have_account-btn text-center">
                    <p>
                      Already have an account?{' '}
                      <Link to={process.env.PUBLIC_URL + '/login'}>Login Here</Link>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>
    </Styles>
  );
}

export default Register;
